/**
 * Vedic astrology constants - signs, nakshatras, dasha sequence, planets.
 */

import * as swe from "./ephemeris";
import { pymod } from "../lib/pyutil";

// Sidereal year in days (more precise than 365.25)
export const SIDEREAL_YEAR_DAYS = 365.25636;

// Total Vimshottari cycle = 120 years
export const VIMSHOTTARI_CYCLE_YEARS = 120;
export const VIMSHOTTARI_CYCLE_DAYS = VIMSHOTTARI_CYCLE_YEARS * SIDEREAL_YEAR_DAYS;

/**
 * Exact nakshatra geometry. 27 nakshatras span the zodiac, each 13°20' = 360/27,
 * subdivided into 4 padas of 3°20' = 360/108. Use the exact fractions (not a
 * truncated 13.333333) so the Moon's nakshatra fraction — and therefore the
 * Vimshottari dasha balance at birth — carries no rounding drift.
 */
export const NAKSHATRA_SPAN_DEG = 360.0 / 27.0;
export const NAKSHATRA_PADA_DEG = 360.0 / 108.0;

export const SIGN_NAMES = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

export const NAKSHATRA_NAMES = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashira", "Ardra",
  "Punarvasu", "Pushya", "Ashlesha", "Magha", "Purva Phalguni", "Uttara Phalguni",
  "Hasta", "Chitra", "Swati", "Vishakha", "Anuradha", "Jyeshtha",
  "Mula", "Purva Ashadha", "Uttara Ashadha", "Shravana", "Dhanishta", "Shatabhisha",
  "Purva Bhadrapada", "Uttara Bhadrapada", "Revati",
];

// Dasha sequence with durations in YEARS
export const DASHA_SEQUENCE: Array<[string, number]> = [
  ["ketu", 7], ["venus", 20], ["sun", 6], ["moon", 10], ["mars", 7],
  ["rahu", 18], ["jupiter", 16], ["saturn", 19], ["mercury", 17],
];

// Pre-compute dasha durations in days for precision
export const DASHA_DAYS: Record<string, number> = Object.fromEntries(
  DASHA_SEQUENCE.map(([lord, years]) => [lord, years * SIDEREAL_YEAR_DAYS])
);

// Swiss Ephemeris planet mappings
export const PLANETS: Array<[string, number]> = [
  ["sun", swe.SUN], ["moon", swe.MOON], ["mercury", swe.MERCURY],
  ["venus", swe.VENUS], ["mars", swe.MARS], ["jupiter", swe.JUPITER],
  ["saturn", swe.SATURN], ["uranus", swe.URANUS], ["neptune", swe.NEPTUNE],
  ["pluto", swe.PLUTO], ["chiron", swe.CHIRON],
];

// Life areas for natal promise calculation
export const LIFE_AREAS = [
  "career", "relationships", "health", "finance",
  "spiritual", "education", "children", "transformation",
];

// Planetary dignities - also in helpers.py for import convenience
export const EXALTATIONS: Record<string, number> = {
  sun: 0, moon: 1, mars: 9, mercury: 5,
  jupiter: 3, venus: 11, saturn: 6,
  rahu: 1, ketu: 7, // 02_planetary_dignities.md: Rahu→Taurus, Ketu→Scorpio (debil auto-derives)
};

export const DEBILITATIONS: Record<string, number> = Object.fromEntries(
  Object.entries(EXALTATIONS).map(([k, v]) => [k, pymod(v + 6, 12)])
);

export const OWN_SIGNS: Record<string, number[]> = {
  sun: [4], moon: [3], mars: [0, 7], mercury: [2, 5],
  jupiter: [8, 11], venus: [1, 6], saturn: [9, 10],
  rahu: [10], ketu: [7], // 02_planetary_dignities.md (Aquarius / Scorpio; SIGN_RULERS unaffected)
};

export const MOOLATRIKONA: Record<string, [number, number, number]> = {
  sun: [4, 0, 20],
  moon: [1, 3, 30], // 02_planetary_dignities.md: >3°–30° Taurus (0°–3° is the exaltation portion)
  mars: [0, 0, 12],
  mercury: [5, 15, 20], // 02_planetary_dignities.md: 15°–20° Virgo
  jupiter: [8, 0, 10],
  venus: [6, 0, 15],
  saturn: [10, 0, 20],
  rahu: [2, 0, 30], ketu: [8, 0, 30], // 02_planetary_dignities.md: Gemini / Sagittarius (whole sign)
};

// Derived from OWN_SIGNS inverse — one ruler per sign (Vedic, no outer planets)
export const SIGN_RULERS: Record<number, string> = {
  0: "mars", 1: "venus", 2: "mercury", 3: "moon", 4: "sun", 5: "mercury",
  6: "venus", 7: "mars", 8: "jupiter", 9: "saturn", 10: "saturn", 11: "jupiter",
};
